import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import 'assets/css/MiniCalendar.css';
import {
  Box,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdCalendarToday } from 'react-icons/md';

const CALENDAR_WIDTH = 350;

const formatYMD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [_y, m, d] = dateStr.split('-');
  return `${d}/${m}/${_y}`;
};

const CommonDatePicker = ({
  labelText,
  textColor,
  brandStars,
  value,
  onChange,
  errors,
  isRequired = true,
  isDisabled = false,
  minDate,
  maxDate,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const selectedDate = value ? new Date(`${value}T12:00:00`) : null;

  const calcPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const calendarHeight = 320;

    let top = rect.bottom + 8;
    let left = rect.left;

    // Flip up if not enough space below
    if (top + calendarHeight > viewportHeight) {
      top = rect.top - calendarHeight - 8;
    }

    // Shift left if would overflow right edge
    if (left + CALENDAR_WIDTH > viewportWidth) {
      left = viewportWidth - CALENDAR_WIDTH - 16;
    }

    setCalendarPos({ top, left });
  }, []);

  const toggleCalendar = () => {
    if (isDisabled) return;
    if (!showCalendar) calcPosition();
    setShowCalendar((prev) => !prev);
  };

  useEffect(() => {
    if (!showCalendar) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };

    const handleScroll = () => {
      setShowCalendar(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showCalendar]);

  const handleDateChange = (date) => {
    onChange(formatYMD(date));
    setShowCalendar(false);
  };

  return (
    <Box ref={containerRef} position="relative">
      <FormLabel
        display="flex"
        ms="4px"
        fontSize="sm"
        fontWeight="500"
        color={textColor}
        mb="8px"
      >
        {labelText}
        {isRequired && <Text color={brandStars}>*</Text>}
      </FormLabel>
      <InputGroup ref={inputRef}>
        <Input
          variant="auth"
          fontSize="sm"
          fontWeight="500"
          size="lg"
          borderRadius="16px !important"
          readOnly
          value={formatDisplay(value)}
          placeholder="DD/MM/YYYY"
          onClick={toggleCalendar}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          isDisabled={isDisabled}
          sx={{ border: '1px solid #ccc', backgroundColor: 'white' }}
        />
        <InputRightElement h="100%" pr="4">
          <Icon
            as={MdCalendarToday}
            color="gray.400"
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            onClick={toggleCalendar}
          />
        </InputRightElement>
      </InputGroup>
      {errors && (
        <p style={{ color: 'red', fontSize: '14px' }}>{errors?.message}</p>
      )}

      {showCalendar && (
        <>
          {/* Backdrop */}
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex="1199"
            onClick={() => setShowCalendar(false)}
          />
          {/* Calendar portal — fixed so no parent overflow clips it */}
          <Box
            position="fixed"
            top={`${calendarPos.top}px`}
            left={`${calendarPos.left}px`}
            zIndex="1200"
            bg="white"
            boxShadow="0px 0px 40px rgba(32, 57, 70, 0.12)"
            borderRadius="20px"
            p="20px"
            width={`${CALENDAR_WIDTH}px`}
          >
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              view="month"
              {...(minDate ? { minDate } : {})}
              {...(maxDate ? { maxDate } : {})}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

CommonDatePicker.propTypes = {
  labelText: PropTypes.string.isRequired,
  textColor: PropTypes.string,
  brandStars: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  isRequired: PropTypes.bool,
  isDisabled: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

export default CommonDatePicker;
